/**
 * Cloud-backed committee sync: committees, memberships, referrals, meetings, docs.
 */

import { getSupabaseClient, hasSupabaseConfig } from '../../lib/supabaseClient';
import { getPocketDB } from '../pocketDb';

function isUuid(id) {
  return (
    typeof id === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)
  );
}

function mapReferralRow(row) {
  return {
    id: row.id,
    type: 'motion',
    motionId: row.source_motion_id || null,
    title: row.title,
    description: row.description || row.title,
    referredBy: row.referred_by || '',
    dateReferred: row.date_referred
      ? new Date(row.date_referred).toLocaleDateString()
      : '',
    instructions: row.instructions || '',
    status: row.status === 'completed' ? 'completed' : row.status === 'in_progress' ? 'in_progress' : 'pending',
    disposition: row.disposition || null,
    recommendations: row.recommendations || '',
    dateCompleted: row.date_completed
      ? new Date(row.date_completed).toLocaleDateString()
      : '',
    reportedToAssembly: Boolean(row.reported_to_assembly),
    reportedDate: row.reported_at ? new Date(row.reported_at).toLocaleDateString() : '',
    reportedAt: row.reported_at || null,
  };
}

function mapMembershipRow(row, profileMap = {}) {
  const profile = profileMap[row.user_id] || {};
  const fromParts = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
  return {
    id: row.id,
    userId: row.user_id,
    role: row.role === 'chair' ? 'chair' : 'member',
    displayName: fromParts || profile.display_name || profile.email || 'User',
    email: profile.email || '',
  };
}

async function loadProfileMap(supabase, userIds) {
  const ids = [...new Set((userIds || []).filter(Boolean))];
  if (!ids.length) return {};
  const { data: profiles } = await supabase
    .from('profiles')
    .select('id, display_name, first_name, last_name')
    .in('id', ids);
  const map = {};
  (profiles || []).forEach((p) => {
    map[p.id] = {
      display_name: p.display_name || '',
      first_name: p.first_name || '',
      last_name: p.last_name || '',
    };
  });
  return map;
}

/** List org login users for committee membership picker. */
export async function listOrgLoginUsers(organizationId) {
  if (!hasSupabaseConfig() || !organizationId) return [];
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('list_org_memberships', {
    p_organization_id: organizationId,
  });
  if (error) {
    console.warn('[committeesSync] list_org_memberships failed', error.message);
    return [];
  }
  return (data || [])
    .filter((row) => row.is_active !== false)
    .map((row) => {
      const fromParts = [row.first_name, row.last_name].filter(Boolean).join(' ');
      return {
        userId: row.user_id,
        displayName: fromParts || row.display_name || row.email || 'User',
        email: row.email || '',
        role: row.role,
        firstName: row.first_name || '',
        lastName: row.last_name || '',
      };
    });
}

/**
 * Pull committees + memberships + referrals into UI shape (and RxDB cache).
 */
export async function loadCommitteesCloud(organizationId) {
  if (!hasSupabaseConfig() || !organizationId) return [];
  const supabase = getSupabaseClient();

  const { data: committees, error } = await supabase
    .from('committees')
    .select('id, organization_id, name, chair_id, chair_user_id, is_standing, description, updated_at')
    .eq('organization_id', organizationId)
    .order('name');

  if (error) {
    console.warn('[committeesSync] committees pull failed', error.message);
    return [];
  }
  if (!committees?.length) return [];

  const committeeIds = committees.map((c) => c.id);

  const [{ data: memberships }, { data: referrals }] = await Promise.all([
    supabase
      .from('committee_memberships')
      .select('id, committee_id, organization_id, user_id, role, updated_at')
      .eq('organization_id', organizationId)
      .in('committee_id', committeeIds),
    supabase
      .from('committee_referrals')
      .select('*')
      .eq('organization_id', organizationId)
      .in('committee_id', committeeIds)
      .order('date_referred', { ascending: false }),
  ]);

  const userIds = [
    ...(memberships || []).map((m) => m.user_id),
    ...committees.map((c) => c.chair_user_id).filter(Boolean),
  ];
  const profileMap = await loadProfileMap(supabase, userIds);

  const membershipsByCommittee = {};
  (memberships || []).forEach((m) => {
    if (!membershipsByCommittee[m.committee_id]) membershipsByCommittee[m.committee_id] = [];
    membershipsByCommittee[m.committee_id].push(mapMembershipRow(m, profileMap));
  });

  const referralsByCommittee = {};
  (referrals || []).forEach((r) => {
    if (!referralsByCommittee[r.committee_id]) referralsByCommittee[r.committee_id] = [];
    referralsByCommittee[r.committee_id].push(mapReferralRow(r));
  });

  const mapped = committees.map((c) => {
    const seats = membershipsByCommittee[c.id] || [];
    const chairSeat = seats.find((s) => s.role === 'chair');
    const chairUserId = c.chair_user_id || chairSeat?.userId || null;
    const chairName =
      (chairUserId && profileMap[chairUserId]?.display_name) ||
      chairSeat?.displayName ||
      '';
    return {
      id: c.id,
      name: c.name,
      type: c.is_standing ? 'standing' : 'special',
      description: c.description || '',
      chairUserId,
      chair: chairName,
      memberships: seats,
      members: seats.map((s) => s.displayName),
      referredItems: referralsByCommittee[c.id] || [],
      created: c.updated_at ? new Date(c.updated_at).toLocaleDateString() : '',
    };
  });

  try {
    const db = await getPocketDB();
    await Promise.all(
      mapped.map((c) =>
        db.committees.upsert({
          id: c.id,
          organization_id: organizationId,
          name: c.name,
          chair_id: c.chairUserId || '',
          is_standing: c.type !== 'special',
          description: c.description || '',
          chair_name: c.chair || '',
          members: c.members || [],
          referred_items: c.referredItems || [],
          updated_at: new Date().toISOString(),
        })
      )
    );
  } catch (err) {
    console.warn('[committeesSync] RxDB cache failed', err?.message || err);
  }

  return mapped;
}

/** Create or update a committee row + replace memberships. */
export async function saveCommitteeCloud(organizationId, committee) {
  if (!hasSupabaseConfig() || !organizationId) return committee;
  const supabase = getSupabaseClient();

  const payload = {
    organization_id: organizationId,
    name: committee.name,
    description: committee.description || '',
    is_standing: committee.type !== 'special' && committee.is_standing !== false,
    chair_user_id: committee.chairUserId || null,
    updated_at: new Date().toISOString(),
  };

  let committeeId = isUuid(committee.id) ? committee.id : null;
  let row = null;

  if (committeeId) {
    const { data, error } = await supabase
      .from('committees')
      .update(payload)
      .eq('id', committeeId)
      .eq('organization_id', organizationId)
      .select('id, organization_id, name, chair_user_id, is_standing, description, updated_at')
      .maybeSingle();
    if (error) {
      console.warn('[committeesSync] update committee failed', error.message);
      throw error;
    }
    row = data;
  }

  if (!row) {
    const { data, error } = await supabase
      .from('committees')
      .insert(payload)
      .select('id, organization_id, name, chair_user_id, is_standing, description, updated_at')
      .single();
    if (error) {
      console.warn('[committeesSync] create committee failed', error.message);
      throw error;
    }
    row = data;
    committeeId = data.id;
  }

  const seats = Array.isArray(committee.memberships) ? committee.memberships : [];
  const desired = seats
    .filter((s) => s.userId)
    .map((s) => ({
      committee_id: committeeId,
      organization_id: organizationId,
      user_id: s.userId,
      role: s.role === 'chair' || s.userId === committee.chairUserId ? 'chair' : 'member',
      updated_at: new Date().toISOString(),
    }));

  // Ensure chair is in memberships
  if (committee.chairUserId && !desired.some((d) => d.user_id === committee.chairUserId)) {
    desired.push({
      committee_id: committeeId,
      organization_id: organizationId,
      user_id: committee.chairUserId,
      role: 'chair',
      updated_at: new Date().toISOString(),
    });
  }

  // Only one chair
  let sawChair = false;
  for (const d of desired) {
    if (d.role === 'chair') {
      if (sawChair) d.role = 'member';
      else sawChair = true;
    }
  }

  const { data: existing } = await supabase
    .from('committee_memberships')
    .select('id, user_id')
    .eq('committee_id', committeeId);

  const desiredUserIds = new Set(desired.map((d) => d.user_id));
  const toDelete = (existing || []).filter((e) => !desiredUserIds.has(e.user_id)).map((e) => e.id);
  if (toDelete.length) {
    await supabase.from('committee_memberships').delete().in('id', toDelete);
  }

  if (desired.length) {
    const { error: upsertErr } = await supabase.from('committee_memberships').upsert(desired, {
      onConflict: 'committee_id,user_id',
    });
    if (upsertErr) {
      console.warn('[committeesSync] membership upsert failed', upsertErr.message);
      throw upsertErr;
    }
  }

  const refreshed = await loadCommitteesCloud(organizationId);
  return refreshed.find((c) => c.id === committeeId) || { ...committee, id: committeeId };
}

export async function createCommitteeReferral({
  organizationId,
  committeeId,
  title,
  description,
  instructions,
  referredBy,
  sourceMotionId,
}) {
  if (!hasSupabaseConfig() || !organizationId || !committeeId) {
    return {
      id: Date.now(),
      type: 'motion',
      motionId: sourceMotionId,
      title,
      description: description || title,
      referredBy: referredBy || '',
      dateReferred: new Date().toLocaleDateString(),
      instructions: instructions || 'No specific instructions',
      status: 'pending',
      recommendations: '',
    };
  }

  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('committee_referrals')
    .insert({
      organization_id: organizationId,
      committee_id: committeeId,
      source_motion_id: sourceMotionId != null ? String(sourceMotionId) : null,
      title,
      description: description || title,
      instructions: instructions || 'No specific instructions',
      referred_by: referredBy || null,
      status: 'pending',
    })
    .select('*')
    .single();

  if (error) {
    console.warn('[committeesSync] create referral failed', error.message);
    throw error;
  }
  return mapReferralRow(data);
}

export async function updateCommitteeReferral(referralId, patches) {
  if (!hasSupabaseConfig() || !isUuid(referralId)) return null;
  const supabase = getSupabaseClient();

  const payload = { updated_at: new Date().toISOString() };
  if (patches.status != null) payload.status = patches.status;
  if (patches.disposition !== undefined) payload.disposition = patches.disposition;
  if (patches.recommendations != null) payload.recommendations = patches.recommendations;
  if (patches.reportedToAssembly != null) {
    payload.reported_to_assembly = Boolean(patches.reportedToAssembly);
    if (patches.reportedToAssembly) {
      payload.reported_at = new Date().toISOString();
    }
  }
  if (patches.status === 'completed') {
    payload.date_completed = new Date().toISOString();
  }

  const { data, error } = await supabase
    .from('committee_referrals')
    .update(payload)
    .eq('id', referralId)
    .select('*')
    .single();

  if (error) {
    console.warn('[committeesSync] update referral failed', error.message);
    throw error;
  }
  return mapReferralRow(data);
}

export async function startCommitteeMeeting({ organizationId, committeeId, createdBy }) {
  if (!hasSupabaseConfig() || !organizationId || !committeeId) {
    return {
      id: `local-${Date.now()}`,
      organizationId,
      committeeId,
      startedAt: new Date().toISOString(),
      endedAt: null,
      minutes: [],
      notes: '',
      attendance: [],
    };
  }
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('committee_meetings')
    .insert({
      organization_id: organizationId,
      committee_id: committeeId,
      created_by: createdBy || null,
      minutes: [],
      notes: '',
    })
    .select('*')
    .single();
  if (error) throw error;
  return {
    id: data.id,
    organizationId: data.organization_id,
    committeeId: data.committee_id,
    startedAt: data.started_at,
    endedAt: data.ended_at,
    minutes: Array.isArray(data.minutes) ? data.minutes : [],
    notes: data.notes || '',
    attendance: [],
  };
}

export async function saveCommitteeMeetingState(meetingId, { notes, minutes, endedAt }) {
  if (!hasSupabaseConfig() || !isUuid(meetingId)) return;
  const supabase = getSupabaseClient();
  const payload = { updated_at: new Date().toISOString() };
  if (notes != null) payload.notes = notes;
  if (minutes != null) payload.minutes = minutes;
  if (endedAt !== undefined) payload.ended_at = endedAt;
  const { error } = await supabase.from('committee_meetings').update(payload).eq('id', meetingId);
  if (error) throw error;
}

export async function saveCommitteeAttendance(meetingId, organizationId, attendanceRows) {
  if (!hasSupabaseConfig() || !isUuid(meetingId) || !organizationId) return;
  const supabase = getSupabaseClient();
  const rows = (attendanceRows || [])
    .filter((r) => r.userId)
    .map((r) => ({
      meeting_id: meetingId,
      organization_id: organizationId,
      user_id: r.userId,
      status: r.status || 'present',
      updated_at: new Date().toISOString(),
    }));
  if (!rows.length) return;
  const { error } = await supabase.from('committee_attendance').upsert(rows, {
    onConflict: 'meeting_id,user_id',
  });
  if (error) throw error;
}

export async function listCommitteeDocuments(committeeId) {
  if (!hasSupabaseConfig() || !committeeId) return [];
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('committee_documents')
    .select('*')
    .eq('committee_id', committeeId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function uploadCommitteeDocument({
  organizationId,
  committeeId,
  file,
  title = null,
  meetingId = null,
  referralId = null,
}) {
  if (!hasSupabaseConfig() || !organizationId || !committeeId || !file) {
    throw new Error('Missing organization, committee, or file');
  }
  const supabase = getSupabaseClient();
  const docId = crypto.randomUUID();
  const safe = String(file.name || 'document')
    .replace(/[^\w.-]+/g, '_')
    .slice(0, 120);
  const path = `committee/${organizationId}/${committeeId}/${docId}/${safe}`;

  const { error: upErr } = await supabase.storage.from('committee-documents').upload(path, file, {
    upsert: false,
    contentType: file.type || 'application/octet-stream',
  });
  if (upErr) throw upErr;

  const {
    data: { user },
  } = await supabase.auth.getUser();

  const { data: row, error: insErr } = await supabase
    .from('committee_documents')
    .insert({
      id: docId,
      organization_id: organizationId,
      committee_id: committeeId,
      meeting_id: meetingId && isUuid(meetingId) ? meetingId : null,
      referral_id: referralId && isUuid(referralId) ? referralId : null,
      title: title || file.name.replace(/\.[^.]+$/, '') || 'Document',
      storage_path: path,
      mime_type: file.type || null,
      byte_size: file.size,
      uploaded_by: user?.id || null,
    })
    .select('*')
    .single();

  if (insErr) {
    await supabase.storage.from('committee-documents').remove([path]).catch(() => {});
    throw insErr;
  }
  return row;
}

export async function getCommitteeDocumentUrl(storagePath) {
  if (!hasSupabaseConfig() || !storagePath) return null;
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.storage
    .from('committee-documents')
    .createSignedUrl(storagePath, 3600);
  if (error) throw error;
  return data?.signedUrl || null;
}
